// Flat-cartoon art direction, 2026 internet-creator edition. Bold black ink
// outlines, SATURATED screen-glow colour (electric blue / neon green / warm
// amber — not pastel), expressive character with personality. Duolingo-energy
// meets primetime-cartoon visual language. The world is a creator's bedroom
// studio — the kid runs an INTERNET economy (content, ecommerce, streaming,
// social, digital products, trading), not a market town.
//
// COPYRIGHT NOTE: original style + character. NOT a tribute to any specific
// show — no protected character designs (no yellow skin + overbite), no logos,
// no trademarked typefaces. Miko has a normal skin tone, a yellow HOODIE (not
// yellow skin), big expressive cartoon eyes, a blue cap + headphones, and his
// own silhouette.

import { type CSSProperties, useEffect, useRef, useState } from 'react';

// Shared ink + the studio / screen-glow palette.
const INK = '#16131f';
const C = {
  // the bedroom-studio room
  wallTop: '#3b2d63',
  wallBot: '#281f4c',
  floor: '#211940',
  deskTop: '#5b4b88',
  deskDk: '#3c3064',
  led1: '#34e0ff', // cyan LED
  led2: '#ff5cc8', // magenta LED
  // screen-glow accents
  blue: '#36b6ff',
  green: '#2fe08a',
  amber: '#ffba2b',
  pink: '#ff5fa8',
  purple: '#9b6bff',
  red: '#ff5252',
  screenDark: '#0c1230',
  // character
  skin: '#f3c49b',
  skinDk: '#dca473',
  hoodie: '#f7b733',
  hoodieDk: '#e09a16',
  cap: '#3b7dd8',
  capDk: '#2a5fad',
  cup: '#211a3a',
  white: '#ffffff',
  cityWin: '#ffd35a',
};

// ─── Mascot: Miko (expressive cartoon, internet-native) ─────────────────────

export type CartoonMikoProps = {
  size?: number;
  mood?: 'happy' | 'thinking' | 'cheering';
  /** Show over-ear headphones (his on-the-internet look). */
  headphones?: boolean;
  style?: CSSProperties;
  className?: string;
};

/** Cartoon Miko, redesigned with a bigger head + more personality: large
 *  expressive eyes with highlights, a cheeky default smirk, yellow hoodie, blue
 *  cap, optional headphones. Original silhouette, normal skin tone. */
export function CartoonMiko({ size = 96, mood = 'happy', headphones, style, className }: CartoonMikoProps) {
  const browY = mood === 'thinking' ? 26 : 24;
  const pupilDX = mood === 'thinking' ? 3 : 0;
  const mouth =
    mood === 'cheering' ? (
      <path d="M41 70 q11 16 22 0 q-11 7 -22 0Z" fill="#9c2f44" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    ) : mood === 'thinking' ? (
      <path d="M45 70 q9 4 16 -2" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    ) : (
      // cheeky asymmetric smirk
      <path d="M41 68 q11 11 22 1" fill="none" stroke={INK} strokeWidth="3.2" strokeLinecap="round" />
    );
  return (
    <svg width={size} height={size * (120 / 104)} viewBox="0 0 104 120" className={className} style={style} aria-label="Miko">
      {/* body / hoodie */}
      <path d="M20 119 q0 -33 32 -33 q32 0 32 33Z" fill={C.hoodie} stroke={INK} strokeWidth="3.6" strokeLinejoin="round" />
      {/* hoodie zip + pocket */}
      <line x1="52" y1="90" x2="52" y2="116" stroke={INK} strokeWidth="2.4" />
      <path d="M36 104 h32 v9 q-16 7 -32 0Z" fill={C.hoodieDk} stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />
      {/* arms */}
      <path d="M20 92 q-11 6 -11 24" fill="none" stroke={INK} strokeWidth="3.6" strokeLinecap="round" />
      <path d="M84 92 q11 6 11 24" fill="none" stroke={INK} strokeWidth="3.6" strokeLinecap="round" />
      <circle cx="9" cy="115" r="6" fill={C.skin} stroke={INK} strokeWidth="3" />
      <circle cx="95" cy="115" r="6" fill={C.skin} stroke={INK} strokeWidth="3" />
      {/* neck */}
      <rect x="45" y="74" width="14" height="14" fill={C.skin} stroke={INK} strokeWidth="3" />
      {/* head — bigger, rounder */}
      <circle cx="52" cy="44" r="39" fill={C.skin} stroke={INK} strokeWidth="3.8" />
      {/* ears (or headphone cups) */}
      {headphones ? (
        <>
          <rect x="6" y="38" width="15" height="22" rx="7" fill={C.purple} stroke={INK} strokeWidth="3.2" />
          <rect x="83" y="38" width="15" height="22" rx="7" fill={C.purple} stroke={INK} strokeWidth="3.2" />
          <path d="M13 36 q39 -36 78 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
        </>
      ) : (
        <>
          <circle cx="15" cy="48" r="6" fill={C.skin} stroke={INK} strokeWidth="3" />
          <circle cx="89" cy="48" r="6" fill={C.skin} stroke={INK} strokeWidth="3" />
        </>
      )}
      {/* cap */}
      <path d="M14 32 q5 -29 38 -29 q33 0 38 29 q-38 -11 -76 0Z" fill={C.cap} stroke={INK} strokeWidth="3.8" strokeLinejoin="round" />
      <path d="M14 32 q-3 5 -12 7 q16 7 33 -2Z" fill={C.capDk} stroke={INK} strokeWidth="3.2" strokeLinejoin="round" />
      <circle cx="52" cy="6" r="4.5" fill={C.capDk} stroke={INK} strokeWidth="2.5" />
      {/* big expressive eyes with highlight */}
      <ellipse cx="39" cy="46" rx="10.5" ry="13" fill="#fff" stroke={INK} strokeWidth="3.2" />
      <ellipse cx="65" cy="46" rx="10.5" ry="13" fill="#fff" stroke={INK} strokeWidth="3.2" />
      <circle cx={39 + pupilDX} cy="49" r="5" fill={INK} />
      <circle cx={65 + pupilDX} cy="49" r="5" fill={INK} />
      <circle cx={37 + pupilDX} cy="46" r="1.8" fill="#fff" />
      <circle cx={63 + pupilDX} cy="46" r="1.8" fill="#fff" />
      {/* brows — expressive */}
      <path d={`M28 ${browY} q9 -6 18 -1`} fill="none" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
      <path d={`M58 ${browY - (mood === 'thinking' ? 3 : 1)} q9 -5 18 1`} fill="none" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
      {/* cheek blush — personality */}
      <ellipse cx="30" cy="60" rx="5" ry="3" fill="#ff9d7a" opacity="0.55" />
      <ellipse cx="74" cy="60" rx="5" ry="3" fill="#ff9d7a" opacity="0.55" />
      {mouth}
    </svg>
  );
}

// ─── World (the creator's bedroom studio) ───────────────────────────────────
// Same prop interface as before so HQ wiring is unchanged. The "stand" is the
// Creator Studio desk (the smoothie stand in a new costume); the "shops" are
// internet-business stations around the room.

export type CartoonWorldProps = {
  shops: { services?: boolean; social?: boolean; gaming?: boolean; trade?: boolean; vendingMachine?: boolean; property?: boolean; broker?: boolean };
  hasStand: boolean;
  showMentorCottage?: boolean;
  hasMentorAdvice?: boolean;
  hintTapStand?: boolean;
  deliveryKey?: number;
  rainKey?: number;
  onOpenStand: () => void;
  onServices?: () => void;
  onSocial?: () => void;
  onGaming?: () => void;
  onTrade?: () => void;
  onProperty?: () => void;
  onBroker?: () => void;
  onRaise?: () => void;
  onMastermind?: () => void;
  stageLabel?: string;
  cashLabel?: string;
};

export function CartoonWorld({
  shops, hasStand, showMentorCottage, hasMentorAdvice, hintTapStand, deliveryKey, rainKey,
  onOpenStand, onServices, onSocial, onGaming, onTrade, onProperty, onBroker, onRaise, onMastermind, stageLabel, cashLabel,
}: CartoonWorldProps) {
  const [coins, setCoins] = useState<number[]>([]);
  const seqRef = useRef(0);
  const handleStandTap = () => {
    if (!hasStand) return;
    const ids = [++seqRef.current, ++seqRef.current, ++seqRef.current];
    setCoins((cs) => [...cs, ...ids]);
    setTimeout(() => onOpenStand(), 600);
  };
  const removeCoin = (id: number) => setCoins((cs) => cs.filter((c) => c !== id));

  const lastDelivery = useRef(deliveryKey ?? 0);
  const lastRain = useRef(rainKey ?? 0);
  const [deliveryActive, setDeliveryActive] = useState(false);
  const [rainActive, setRainActive] = useState(false);
  useEffect(() => {
    if (deliveryKey === undefined) return;
    if (deliveryKey !== lastDelivery.current) {
      lastDelivery.current = deliveryKey;
      setDeliveryActive(true);
      const t = setTimeout(() => setDeliveryActive(false), 4200);
      return () => clearTimeout(t);
    }
  }, [deliveryKey]);
  useEffect(() => {
    if (rainKey === undefined) return;
    if (rainKey !== lastRain.current) {
      lastRain.current = rainKey;
      setRainActive(true);
      const t = setTimeout(() => setRainActive(false), 4500);
      return () => clearTimeout(t);
    }
  }, [rainKey]);

  // Each venture is a DISTINCT object that lives somewhere in the room (not a
  // menu tile) — you tap the thing itself to go run it. Each has a fixed home
  // spot so the room reads as a real place; only active ventures are present,
  // so the studio visibly fills up as the kid's empire grows.
  // (cashLabel is shown by the "My money" card below the world, so the world
  // doesn't repeat it.)
  void cashLabel;
  const roomObjects: Array<{ key: string; active: boolean; x: number; y: number; onClick?: () => void; el: React.ReactNode }> = [
    { key: 'trade', active: !!shops.trade, x: 110, y: 14, onClick: onTrade, el: <TradingObj /> },
    { key: 'gaming', active: !!shops.gaming, x: 8, y: 92, onClick: onGaming, el: <StreamRigObj /> },
    { key: 'services', active: !!shops.services, x: 240, y: 96, onClick: onServices, el: <StoreObj /> },
    { key: 'social', active: !!shops.social, x: 46, y: 150, onClick: onSocial, el: <PhoneTripodObj /> },
    { key: 'vending', active: !!shops.vendingMachine, x: 250, y: 150, onClick: undefined, el: <TabletObj /> },
  ];

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg viewBox="0 0 320 200" width="100%" height="auto" style={{ display: 'block' }} aria-label="Your creator studio">
        <defs>
          <linearGradient id="cw-wall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.wallTop} />
            <stop offset="100%" stopColor={C.wallBot} />
          </linearGradient>
          <linearGradient id="cw-led" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={C.led1} />
            <stop offset="50%" stopColor={C.led2} />
            <stop offset="100%" stopColor={C.led1} />
          </linearGradient>
        </defs>

        {/* room wall + floor */}
        <rect x="0" y="0" width="320" height="200" fill="url(#cw-wall)" />
        <rect x="0" y="158" width="320" height="42" fill={C.floor} />
        <line x1="0" y1="158" x2="320" y2="158" stroke={INK} strokeWidth="3" opacity="0.5" />

        {/* LED strip along the ceiling — pulses cyan↔magenta */}
        <rect x="10" y="6" width="300" height="7" rx="3.5" fill="url(#cw-led)" stroke={INK} strokeWidth="2.5">
          <animate attributeName="opacity" values="0.75;1;0.75" dur="2.4s" repeatCount="indefinite" />
        </rect>

        {/* window to the night city (upper-left) — once Property unlocks, the
            city outside IS the market: a FOR-SALE sign appears and tapping the
            window opens the property board. */}
        <g
          style={{ cursor: shops.property && onProperty ? 'pointer' : 'default' }}
          onClick={shops.property ? onProperty : undefined}
        >
          <CartoonWindow x={8} y={22} />
          {shops.property && <ForSaleSign x={54} y={70} />}
        </g>

        {/* back wall: the deals corkboard once Broker unlocks, else a decor poster */}
        {shops.broker ? (
          <g transform="translate(203, 56)" style={{ cursor: onBroker ? 'pointer' : 'default' }} onClick={onBroker}>
            <DealsBoardObj />
          </g>
        ) : (
          <WallPoster x={210} y={64} glyph="▶" fill={C.red} />
        )}

        {/* rain / "views dropping" storm on a loss week */}
        {rainActive && <CartoonStorm />}

        {/* mentor video-call monitor — right wall */}
        {showMentorCottage && (
          <g transform="translate(254, 16)" style={{ cursor: onMastermind ? 'pointer' : 'default' }} onClick={onMastermind}>
            <CartoonMentorScreen glowing={!!hasMentorAdvice} />
          </g>
        )}

        {/* the Creator Studio recording desk (the hero — "the stand" re-costumed) */}
        {hasStand ? (
          <g transform="translate(96, 70)" style={{ cursor: 'pointer' }} onClick={handleStandTap}>
            <CartoonCreatorDesk />
            {hintTapStand && <TapRing />}
            {coins.map((id, i) => (
              <CartoonCoin key={id} delayMs={i * 120} onDone={() => removeCoin(id)} />
            ))}
          </g>
        ) : (
          <g transform="translate(112, 80)" style={{ cursor: onRaise ? 'pointer' : 'default' }} onClick={onRaise}>
            <CartoonStartChannel />
          </g>
        )}

        {/* venture OBJECTS, each in its natural spot in the room — tap the thing
            itself (a streaming rig, a store screen, a phone on a tripod…) to go
            run that venture. */}
        {roomObjects.filter((o) => o.active).map((o) => (
          <g key={o.key} transform={`translate(${o.x}, ${o.y})`} style={{ cursor: o.onClick ? 'pointer' : 'default' }} onClick={o.onClick}>
            {o.el}
          </g>
        ))}

        {/* stage label banner — a small floor sign */}
        {stageLabel && (
          <g transform="translate(160, 176)">
            <CartoonBanner label={stageLabel.toUpperCase()} />
          </g>
        )}

        {/* delivery drone on a new asset */}
        {deliveryActive && <CartoonDrone />}
      </svg>
    </div>
  );
}

// ─── Studio scenery ─────────────────────────────────────────────────────────

/** A window onto a neon night city. */
function CartoonWindow({ x, y }: { x: number; y: number }) {
  const windows: Array<[number, number]> = [
    [10, 18], [16, 26], [26, 14], [34, 30], [42, 20], [50, 28], [60, 16], [68, 24], [76, 30],
  ];
  return (
    <g transform={`translate(${x},${y})`}>
      {/* frame + night sky */}
      <rect x="0" y="0" width="92" height="64" rx="6" fill="#0d1640" stroke={INK} strokeWidth="3.5" />
      {/* moon */}
      <circle cx="74" cy="14" r="7" fill="#ffe9a8" stroke={INK} strokeWidth="2" />
      {/* skyline silhouettes */}
      <path d="M2 64 V40 h10 V30 h10 V46 h12 V26 h12 V42 h12 V34 h12 V48 h12 V38 h2 V64Z" fill="#1b2a66" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      {/* lit windows */}
      {windows.map(([wx, wy], i) => (
        <rect key={i} x={wx} y={wy + 24} width="3" height="3" fill={C.cityWin} opacity={0.9}>
          <animate attributeName="opacity" values="0.4;1;0.4" dur={`${2 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.2}s`} />
        </rect>
      ))}
      {/* mullions */}
      <line x1="46" y1="0" x2="46" y2="64" stroke={INK} strokeWidth="3" />
      <line x1="0" y1="32" x2="92" y2="32" stroke={INK} strokeWidth="3" />
    </g>
  );
}

function WallPoster({ x, y, glyph, fill }: { x: number; y: number; glyph: string; fill: string }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x="0" y="0" width="28" height="34" rx="3" fill={fill} stroke={INK} strokeWidth="3" />
      <text x="14" y="24" fill={INK} fontSize="18" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">{glyph}</text>
    </g>
  );
}

/** Little "4 SALE" sign hanging inside the window — the Property tap target cue. */
function ForSaleSign({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <line x1="20" y1="-6" x2="20" y2="0" stroke={INK} strokeWidth="2.2" />
      <rect x="0" y="0" width="40" height="13" rx="3" fill={C.red} stroke={INK} strokeWidth="2.6" />
      <text x="20" y="9.8" fill="#fff" fontSize="8.5" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">4 SALE</text>
    </g>
  );
}

/** Deals corkboard — pinned notes + a handshake, the Broker tap target. */
function DealsBoardObj() {
  return (
    <g>
      <rect x="0" y="0" width="42" height="34" rx="3" fill="#8a6a3f" stroke={INK} strokeWidth="3" />
      <rect x="3" y="3" width="36" height="28" rx="2" fill="#c9a06a" />
      {/* pinned notes */}
      <rect x="6" y="6" width="11" height="10" fill="#fffdf5" stroke={INK} strokeWidth="1.4" transform="rotate(-6 11 11)" />
      <rect x="25" y="7" width="11" height="10" fill={C.amber} stroke={INK} strokeWidth="1.4" transform="rotate(5 30 12)" />
      <rect x="15" y="17" width="12" height="11" fill={C.green} stroke={INK} strokeWidth="1.4" transform="rotate(-3 21 22)" />
      <text x="21" y="26.5" fontSize="8" textAnchor="middle">🤝</text>
      <RoomLabel cx={21} y={37} text="Deals" />
    </g>
  );
}

function CartoonBanner({ label, tone = 'default' }: { label: string; tone?: 'default' | 'cash' }) {
  const bg = tone === 'cash' ? C.green : C.amber;
  const w = Math.max(46, label.length * 8 + 16);
  return (
    <g transform={`translate(${-w / 2}, 0)`}>
      <rect x="0" y="0" width={w} height="20" rx="6" fill={bg} stroke={INK} strokeWidth="3" />
      <text x={w / 2} y="14.5" fill={INK} fontSize="11" fontFamily="Nunito, system-ui, sans-serif" fontWeight="900" textAnchor="middle">{label}</text>
    </g>
  );
}

/** The Creator Studio hero — Miko in a gaming chair at a desk with dual
 *  monitors, a ring light, and a laptop. Bright, glowing, "ON AIR". */
function CartoonCreatorDesk() {
  return (
    <g>
      {/* floor shadow */}
      <ellipse cx="64" cy="92" rx="64" ry="7" fill="#000" opacity="0.18" />

      {/* ring light on a stand (left) */}
      <rect x="2" y="20" width="4" height="62" fill={C.deskDk} stroke={INK} strokeWidth="2.5" />
      <circle cx="4" cy="18" r="15" fill="none" stroke={C.amber} strokeWidth="6" />
      <circle cx="4" cy="18" r="15" fill="none" stroke={INK} strokeWidth="2.5" />
      <circle cx="4" cy="18" r="9" fill="#fff6da" opacity="0.5" />

      {/* camera on a tripod, filming Miko (lens points right, toward him) */}
      <g transform="translate(16, 34)">
        <line x1="11" y1="14" x2="4" y2="32" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
        <line x1="11" y1="14" x2="18" y2="32" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
        <line x1="11" y1="14" x2="11" y2="30" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
        <rect x="0" y="0" width="20" height="15" rx="3" fill="#1a1530" stroke={INK} strokeWidth="2.8" />
        <rect x="20" y="3" width="5" height="9" rx="1.5" fill="#1a1530" stroke={INK} strokeWidth="2.4" />
        <circle cx="22" cy="7.5" r="3.4" fill={C.blue} stroke={INK} strokeWidth="2" />
        <circle cx="5" cy="3.5" r="1.8" fill={C.red}>
          <animate attributeName="opacity" values="1;0.2;1" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* gaming chair back behind Miko */}
      <rect x="44" y="6" width="40" height="56" rx="12" fill={C.purple} stroke={INK} strokeWidth="3.4" />
      <rect x="50" y="14" width="28" height="40" rx="8" fill="#7b4ff0" stroke={INK} strokeWidth="2.4" />
      <rect x="56" y="6" width="16" height="8" rx="4" fill={C.purple} stroke={INK} strokeWidth="2.4" />

      {/* Miko seated (head + torso peek over the desk) */}
      <g transform="translate(40, 8)">
        <svg x="0" y="0" width="48" height="55" viewBox="0 0 104 120"><CartoonMiko size={48} headphones mood="cheering" /></svg>
      </g>

      {/* desk surface */}
      <rect x="0" y="66" width="128" height="14" rx="4" fill={C.deskTop} stroke={INK} strokeWidth="3.4" />
      <rect x="10" y="80" width="6" height="14" fill={C.deskDk} stroke={INK} strokeWidth="2.4" />
      <rect x="112" y="80" width="6" height="14" fill={C.deskDk} stroke={INK} strokeWidth="2.4" />

      {/* dual monitors facing the viewer */}
      <g transform="translate(86, 30)">
        <rect x="0" y="0" width="40" height="30" rx="4" fill={C.screenDark} stroke={INK} strokeWidth="3.2" />
        <rect x="4" y="4" width="32" height="22" rx="2" fill={C.blue} />
        {/* play button */}
        <path d="M16 9 L26 15 L16 21Z" fill="#fff" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
        <rect x="18" y="60" width="4" height="6" fill={C.deskDk} stroke={INK} strokeWidth="2" />
      </g>

      {/* laptop on the desk (front) */}
      <g transform="translate(30, 52)">
        <rect x="0" y="0" width="34" height="20" rx="3" fill={C.screenDark} stroke={INK} strokeWidth="3" />
        <rect x="3" y="3" width="28" height="14" rx="1.5" fill={C.green} />
        <path d="M-4 20 h42 l3 6 h-48Z" fill="#cfd6f0" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      </g>

      {/* ON AIR sign */}
      <g transform="translate(96, 12)">
        <rect x="0" y="0" width="34" height="14" rx="4" fill={C.red} stroke={INK} strokeWidth="2.5" />
        <text x="17" y="10.5" fill="#fff" fontSize="8" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">ON AIR</text>
        <animate attributeName="opacity" values="1;0.4;1" dur="1.4s" repeatCount="indefinite" />
      </g>
    </g>
  );
}

/** Small name chip that sits under a room object (centred on x). */
function RoomLabel({ cx, y, text }: { cx: number; y: number; text: string }) {
  const w = text.length * 5.6 + 14;
  return (
    <g transform={`translate(${cx - w / 2}, ${y})`}>
      <rect x="0" y="0" width={w} height="13" rx="6" fill={C.white} stroke={INK} strokeWidth="2.2" />
      <text x={w / 2} y="9.7" fill={INK} fontSize="9" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">{text}</text>
    </g>
  );
}

/** Trading — a wall-mounted screen with a rising green chart. */
function TradingObj() {
  return (
    <g>
      <rect x="0" y="0" width="58" height="40" rx="5" fill={C.screenDark} stroke={INK} strokeWidth="3.2" />
      <rect x="4" y="4" width="50" height="28" rx="2" fill="#0c1230" />
      <polyline points="9,27 19,22 27,24 38,13 49,8" fill="none" stroke={C.green} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M44 8 l6 -1 l-2 6Z" fill={C.green} stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
      <rect x="26" y="40" width="6" height="4" fill={C.deskDk} />
      <RoomLabel cx={29} y={45} text="Skins" />
    </g>
  );
}

/** Stream Deck — a PC tower with RGB + a monitor and a boom mic. */
function StreamRigObj() {
  return (
    <g>
      <ellipse cx="30" cy="60" rx="30" ry="4.5" fill="#000" opacity="0.2" />
      {/* monitor on stand */}
      <rect x="18" y="4" width="38" height="28" rx="4" fill={C.screenDark} stroke={INK} strokeWidth="3" />
      <rect x="22" y="8" width="30" height="20" rx="2" fill={C.purple} />
      <path d="M32 13 L42 18 L32 23Z" fill="#fff" stroke={INK} strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="35" y="32" width="4" height="8" fill={C.deskDk} stroke={INK} strokeWidth="2" />
      <rect x="26" y="40" width="22" height="4" rx="2" fill={C.deskDk} stroke={INK} strokeWidth="2" />
      {/* PC tower with RGB slits */}
      <rect x="0" y="20" width="15" height="36" rx="3" fill="#1a1530" stroke={INK} strokeWidth="3" />
      <rect x="3" y="25" width="9" height="3" fill={C.led1} />
      <rect x="3" y="30" width="9" height="3" fill={C.led2} />
      <rect x="3" y="35" width="9" height="3" fill={C.green} />
      <circle cx="7.5" cy="49" r="2" fill={C.green} />
      {/* boom mic */}
      <line x1="56" y1="2" x2="48" y2="16" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
      <ellipse cx="47" cy="18" rx="5" ry="6" fill="#3a3358" stroke={INK} strokeWidth="2.4" />
      <RoomLabel cx={28} y={45} text="Beats" />
    </g>
  );
}

/** Online Store — a monitor showing a shopping bag, with a parcel stack. */
function StoreObj() {
  return (
    <g>
      <ellipse cx="30" cy="58" rx="30" ry="4.5" fill="#000" opacity="0.2" />
      {/* parcels */}
      <rect x="0" y="40" width="16" height="14" rx="2" fill={C.amber} stroke={INK} strokeWidth="2.6" />
      <line x1="8" y1="40" x2="8" y2="54" stroke={INK} strokeWidth="1.6" />
      <rect x="2" y="30" width="12" height="11" rx="2" fill="#ffd06a" stroke={INK} strokeWidth="2.4" />
      {/* monitor */}
      <rect x="18" y="4" width="40" height="30" rx="4" fill={C.screenDark} stroke={INK} strokeWidth="3" />
      <rect x="22" y="8" width="32" height="22" rx="2" fill={C.green} />
      {/* shopping bag glyph */}
      <path d="M32 14 h12 l-2 13 h-8Z" fill="#fff" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M35 14 a3 3 0 0 1 6 0" fill="none" stroke={INK} strokeWidth="2" />
      <rect x="36" y="34" width="4" height="8" fill={C.deskDk} stroke={INK} strokeWidth="2" />
      <rect x="27" y="42" width="22" height="4" rx="2" fill={C.deskDk} stroke={INK} strokeWidth="2" />
      <RoomLabel cx={34} y={47} text="Agency" />
    </g>
  );
}

/** Social Hub — a phone on a tripod (the selfie/posting setup). */
function PhoneTripodObj() {
  return (
    <g>
      <ellipse cx="22" cy="62" rx="22" ry="4.5" fill="#000" opacity="0.2" />
      {/* tripod legs */}
      <line x1="22" y1="38" x2="8" y2="60" stroke={C.deskDk} strokeWidth="3.4" strokeLinecap="round" />
      <line x1="22" y1="38" x2="36" y2="60" stroke={C.deskDk} strokeWidth="3.4" strokeLinecap="round" />
      <line x1="22" y1="38" x2="22" y2="56" stroke={C.deskDk} strokeWidth="3.4" strokeLinecap="round" />
      {/* phone (portrait) */}
      <rect x="11" y="0" width="22" height="38" rx="4" fill={C.screenDark} stroke={INK} strokeWidth="3" />
      <rect x="14" y="4" width="16" height="30" rx="2" fill={C.pink} />
      <path d="M22 14 c-3 -4 -8 -1 -8 3 c0 4 8 9 8 9 c0 0 8 -5 8 -9 c0 -4 -5 -7 -8 -3Z" fill="#fff" stroke={INK} strokeWidth="1.8" strokeLinejoin="round" />
      <RoomLabel cx={22} y={48} text="Clips" />
    </g>
  );
}

/** Digital Products — a drawing tablet with colour swatches + a stylus. */
function TabletObj() {
  return (
    <g>
      <ellipse cx="24" cy="44" rx="24" ry="4" fill="#000" opacity="0.2" />
      <rect x="0" y="6" width="46" height="30" rx="4" fill="#1a1530" stroke={INK} strokeWidth="3" />
      <rect x="4" y="10" width="38" height="22" rx="2" fill={C.amber} />
      <circle cx="13" cy="21" r="3.4" fill={C.pink} stroke={INK} strokeWidth="1.6" />
      <circle cx="23" cy="21" r="3.4" fill={C.blue} stroke={INK} strokeWidth="1.6" />
      <circle cx="33" cy="21" r="3.4" fill={C.green} stroke={INK} strokeWidth="1.6" />
      <line x1="40" y1="4" x2="52" y2="-6" stroke={INK} strokeWidth="3.2" strokeLinecap="round" />
      <RoomLabel cx={23} y={39} text="Course" />
    </g>
  );
}

/** Mentor as a wall-mounted video-call monitor; glows when fresh advice. */
function CartoonMentorScreen({ glowing }: { glowing: boolean }) {
  return (
    <g>
      {/* monitor */}
      <rect x="0" y="0" width="56" height="42" rx="6" fill={C.screenDark} stroke={INK} strokeWidth="3.5" />
      <rect x="5" y="5" width="46" height="28" rx="3" fill={C.blue} />
      {/* mentor face (simple, friendly) */}
      <circle cx="28" cy="19" r="9" fill={C.skin} stroke={INK} strokeWidth="2.4" />
      <circle cx="25" cy="18" r="1.6" fill={INK} />
      <circle cx="31" cy="18" r="1.6" fill={INK} />
      <path d="M24 22 q4 3 8 0" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      {/* label */}
      <rect x="12" y="42" width="32" height="13" rx="6" fill={C.amber} stroke={INK} strokeWidth="2.4" />
      <text x="28" y="52" fill={INK} fontSize="8" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">MENTOR</text>
      {glowing && (
        <g>
          <circle cx="28" cy="20" r="38" fill={C.amber} opacity="0.16">
            <animate attributeName="opacity" values="0.08;0.28;0.08" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <g transform="translate(40, -14)">
            <animateTransform attributeName="transform" type="translate" values="40,-14; 40,-18; 40,-14" dur="1.2s" repeatCount="indefinite" />
            <rect x="0" y="0" width="22" height="18" rx="6" fill={C.amber} stroke={INK} strokeWidth="2.5" />
            <path d="M6 18 l4 6 l4 -6Z" fill={C.amber} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
            <rect x="9.5" y="4" width="3" height="7" rx="1.5" fill={INK} />
            <rect x="9.5" y="13" width="3" height="3" rx="1.5" fill={INK} />
          </g>
        </g>
      )}
    </g>
  );
}

/** Empty-desk "start your channel" call to action (pre-first-business). */
function CartoonStartChannel() {
  return (
    <g>
      <ellipse cx="40" cy="66" rx="42" ry="5" fill="#000" opacity="0.18" />
      {/* bare monitor */}
      <rect x="14" y="18" width="52" height="38" rx="6" fill={C.screenDark} stroke={INK} strokeWidth="3.4" />
      <rect x="19" y="23" width="42" height="28" rx="3" fill="#1a2350" />
      <path d="M34 30 L48 37 L34 44Z" fill={C.amber} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <rect x="36" y="56" width="8" height="8" fill={C.deskDk} stroke={INK} strokeWidth="2.4" />
      {/* sign */}
      <g transform="translate(40, 2)">
        <rect x="-34" y="-2" width="68" height="22" rx="6" fill={C.amber} stroke={INK} strokeWidth="3.4" />
        <text x="0" y="12" fill={INK} fontSize="10" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">GO LIVE!</text>
      </g>
      <text x="40" y="78" fill="#fff" fontSize="9" fontFamily="Nunito, sans-serif" fontWeight="800" textAnchor="middle" opacity="0.9">tap to start</text>
    </g>
  );
}

function TapRing() {
  return (
    <g pointerEvents="none">
      <rect x="-6" y="14" width="138" height="70" rx="14" fill="none" stroke={C.amber} strokeWidth="4">
        <animate attributeName="opacity" values="0.35;1;0.35" dur="1.1s" repeatCount="indefinite" />
        <animate attributeName="stroke-width" values="3;6;3" dur="1.1s" repeatCount="indefinite" />
      </rect>
      <g transform="translate(54, 90)">
        <animateTransform attributeName="transform" type="translate" values="54,90; 54,84; 54,90" dur="0.9s" repeatCount="indefinite" />
        <rect x="0" y="0" width="34" height="16" rx="8" fill={C.amber} stroke={INK} strokeWidth="2.5" />
        <text x="17" y="12" fill={INK} fontSize="9" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">TAP!</text>
      </g>
    </g>
  );
}

function CartoonCoin({ delayMs, onDone }: { delayMs: number; onDone: () => void }) {
  const driftX = 56 + Math.round((Math.random() - 0.5) * 28);
  useEffect(() => {
    const t = setTimeout(onDone, delayMs + 950);
    return () => clearTimeout(t);
  }, [delayMs, onDone]);
  return (
    <g pointerEvents="none">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,-26; 0,-38" dur="0.95s" begin={`${delayMs}ms`} fill="freeze" />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.7;1" dur="0.95s" begin={`${delayMs}ms`} fill="freeze" />
        <circle cx={driftX} cy={36} r="7" fill={C.amber} stroke={INK} strokeWidth="2.5" />
        <text x={driftX} y={39} fill={INK} fontSize="8" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">$</text>
      </g>
    </g>
  );
}

/** Delivery DRONE on a new asset (the internet-economy version of the van). */
function CartoonDrone() {
  return (
    <g pointerEvents="none">
      <g>
        <animateTransform attributeName="transform" type="translate" values="-90,40; 110,70; 360,50" keyTimes="0;0.55;1" dur="4s" fill="freeze" />
        {/* rotors */}
        <line x1="-6" y1="0" x2="14" y2="0" stroke={INK} strokeWidth="3" strokeLinecap="round" />
        <line x1="40" y1="0" x2="60" y2="0" stroke={INK} strokeWidth="3" strokeLinecap="round" />
        <ellipse cx="4" cy="0" rx="10" ry="3" fill={C.led1} stroke={INK} strokeWidth="2" opacity="0.8" />
        <ellipse cx="50" cy="0" rx="10" ry="3" fill={C.led1} stroke={INK} strokeWidth="2" opacity="0.8" />
        {/* body */}
        <rect x="16" y="2" width="22" height="12" rx="4" fill={C.blue} stroke={INK} strokeWidth="3" />
        {/* parcel */}
        <rect x="20" y="16" width="14" height="12" rx="2" fill={C.amber} stroke={INK} strokeWidth="2.5" />
        <line x1="27" y1="16" x2="27" y2="28" stroke={INK} strokeWidth="1.5" />
        <line x1="16" y1="14" x2="20" y2="16" stroke={INK} strokeWidth="2" />
        <line x1="38" y1="14" x2="34" y2="16" stroke={INK} strokeWidth="2" />
      </g>
    </g>
  );
}

/** Loss-week "views dropping" storm — a glitchy cloud trailing red down-arrows. */
function CartoonStorm() {
  return (
    <g pointerEvents="none">
      <g>
        <animateTransform attributeName="transform" type="translate" values="340,20; -130,20" dur="4.5s" fill="freeze" />
        <path d="M0 16 q-2 -16 18 -13 q4 -13 22 -6 q15 -6 19 9 q15 0 11 13Z" fill="#6b6f86" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        {[12, 28, 44].map((x, i) => (
          <g key={i}>
            <path d={`M${x} 20 v10 m-4 -4 l4 4 l4 -4`} fill="none" stroke={C.red} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="opacity" values="1;0" dur="0.6s" begin={`${i * 0.14}s`} repeatCount="indefinite" />
              <animateTransform attributeName="transform" type="translate" values="0,0; 0,16" dur="0.6s" begin={`${i * 0.14}s`} repeatCount="indefinite" />
            </path>
          </g>
        ))}
      </g>
    </g>
  );
}

// ─── Studio scene strips (atmosphere above each screen's data) ──────────────
// Height-capped, fluid-width interior vignettes. Same rule as before: the art
// is atmosphere; the data below it is the message.

function StudioStrip({ children, height = 120 }: { children: React.ReactNode; height?: number }) {
  return (
    <svg viewBox={`0 0 320 ${height}`} width="100%" style={{ display: 'block', maxHeight: height + 16 }} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
      <defs>
        <linearGradient id="cs-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.wallTop} />
          <stop offset="100%" stopColor={C.wallBot} />
        </linearGradient>
        <linearGradient id="cs-led" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={C.led1} />
          <stop offset="50%" stopColor={C.led2} />
          <stop offset="100%" stopColor={C.led1} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height={height} fill="url(#cs-wall)" />
      <rect x="0" y={height - 22} width="320" height="22" fill={C.floor} />
      <rect x="10" y="5" width="300" height="6" rx="3" fill="url(#cs-led)" stroke={INK} strokeWidth="2" />
      {children}
    </svg>
  );
}

/** A desk + glowing monitor that shows a venture glyph. */
function DeskMonitor({ x, glyph, fill, caption }: { x: number; glyph: string; fill: string; caption: string }) {
  return (
    <g transform={`translate(${x}, 32)`}>
      <rect x="0" y="0" width="58" height="42" rx="6" fill={C.screenDark} stroke={INK} strokeWidth="3.4" />
      <rect x="5" y="5" width="48" height="32" rx="3" fill={fill} />
      <text x="29" y="30" fontSize="20" textAnchor="middle">{glyph}</text>
      <rect x="26" y="42" width="6" height="8" fill={C.deskDk} stroke={INK} strokeWidth="2.4" />
      <rect x="-6" y="50" width="70" height="9" rx="3" fill={C.deskTop} stroke={INK} strokeWidth="3" />
      <text x="29" y="74" fill="#fff" fontSize="10" fontFamily="Nunito, sans-serif" fontWeight="900" textAnchor="middle">{caption}</text>
    </g>
  );
}

/** Miko standing in a scene with a soft floor shadow + headphones. */
function CartoonMikoStudio({ size = 48, mood }: { size?: number; mood?: 'happy' | 'thinking' | 'cheering' }) {
  return (
    <g>
      <ellipse cx={size / 2} cy={size + 2} rx={size / 2.4} ry="3.5" fill="#000" opacity="0.2" />
      <svg x="0" y="0" width={size} height={size * (120 / 104)} viewBox="0 0 104 120"><CartoonMiko size={size} mood={mood} headphones /></svg>
    </g>
  );
}

export function CartoonBusinessScene() {
  // Creator Studio
  return (
    <StudioStrip>
      <CartoonWindow x={6} y={16} />
      <DeskMonitor x={120} glyph="🔥" fill={C.red} caption="DROP STORE" />
      <g transform="translate(232, 40)"><CartoonMikoStudio size={50} mood="cheering" /></g>
    </StudioStrip>
  );
}
export function CartoonServicesScene() {
  // Online Store
  return (
    <StudioStrip>
      <WallPoster x={20} y={20} glyph="$" fill={C.green} />
      <DeskMonitor x={120} glyph="🤖" fill={C.green} caption="AI AGENCY" />
      {/* parcels */}
      <g transform="translate(40, 70)">
        <rect x="0" y="0" width="22" height="20" rx="2" fill={C.amber} stroke={INK} strokeWidth="3" />
        <line x1="11" y1="0" x2="11" y2="20" stroke={INK} strokeWidth="2" />
      </g>
      <g transform="translate(232, 40)"><CartoonMikoStudio size={50} /></g>
    </StudioStrip>
  );
}
export function CartoonSocialScene() {
  // Social Hub
  return (
    <StudioStrip>
      <CartoonWindow x={6} y={16} />
      <DeskMonitor x={120} glyph="🎬" fill={C.pink} caption="CLIP FARM" />
      <g transform="translate(232, 40)"><CartoonMikoStudio size={50} mood="cheering" /></g>
    </StudioStrip>
  );
}
export function CartoonGamingScene() {
  // Stream Deck
  return (
    <StudioStrip>
      <WallPoster x={20} y={20} glyph="▶" fill={C.purple} />
      <DeskMonitor x={120} glyph="🎧" fill={C.purple} caption="BEAT LAB" />
      <g transform="translate(232, 40)"><CartoonMikoStudio size={50} mood="thinking" /></g>
    </StudioStrip>
  );
}
export function CartoonTradeScene() {
  // Trading Screen
  return (
    <StudioStrip>
      <CartoonWindow x={6} y={16} />
      <DeskMonitor x={120} glyph="💎" fill={C.blue} caption="SKIN TRADE" />
      <g transform="translate(232, 40)"><CartoonMikoStudio size={50} /></g>
    </StudioStrip>
  );
}

/** Freedom Meter atmosphere — the neon night city beyond the window rises and
 *  lights up as freedom% climbs (passive income building your skyline). */
export function CartoonFreedomSky({ freedomPct, freedomMode }: { freedomPct: number; freedomMode: boolean }) {
  const pct = Math.max(0, Math.min(120, freedomPct));
  const base = pct <= 5 ? 0 : Math.min(6, 1 + Math.floor((pct - 5) / 18));
  const count = freedomMode ? Math.min(7, base + 1) : base;
  return (
    <svg viewBox="0 0 320 80" width="100%" style={{ display: 'block', maxHeight: 96 }} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
      <defs>
        <linearGradient id="cf-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10183f" />
          <stop offset="100%" stopColor="#2a2150" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height="80" fill="url(#cf-sky)" />
      {/* moon / glow */}
      <circle cx="288" cy="18" r={freedomMode ? 11 : 8} fill={freedomMode ? '#ffe9a8' : '#cdd6ff'} stroke={INK} strokeWidth="2.5" />
      {/* ground line */}
      <rect x="0" y="66" width="320" height="14" fill={C.floor} />
      <line x1="0" y1="66" x2="320" y2="66" stroke={INK} strokeWidth="2.5" opacity="0.6" />
      {Array.from({ length: count }).map((_, i) => {
        const gap = (320 - 40) / (count + 1);
        const cx = 20 + gap * (i + 1);
        const h = Math.round(14 + pct * 0.28);
        const w = 26;
        const top = 66 - Math.min(50, Math.max(12, h));
        const fill = i % 3 === 0 ? C.green : i % 3 === 1 ? C.amber : C.blue;
        return (
          <g key={i}>
            <rect x={cx - w / 2} y={top} width={w} height={66 - top} fill={fill} stroke={INK} strokeWidth="3" />
            {/* lit windows grid */}
            {Array.from({ length: 3 }).map((_, r) =>
              Array.from({ length: 2 }).map((_, c2) => (
                <rect key={`${r}-${c2}`} x={cx - w / 2 + 5 + c2 * 11} y={top + 6 + r * 9} width="5" height="5" fill={INK} opacity="0.55" />
              )),
            )}
          </g>
        );
      })}
      {count === 0 && (
        <text x="160" y="50" fill="#cdd6ff" fontSize="11" fontFamily="Nunito, sans-serif" fontWeight="800" textAnchor="middle" opacity="0.7">
          build passive income to grow your city
        </text>
      )}
    </svg>
  );
}
